/**
 * Unit conversions and small math helpers.
 *
 * House rule for this project: carrier aviation is measured in feet, knots and
 * degrees, and converting a 3.5 degree glideslope into metres helps nobody. So
 * the pattern, the deck geometry and the aerodynamics are all imperial.
 * Three.js speaks metres because that is what every renderer assumes.
 * Conversion happens where the two meet — RailsFlight — and nowhere else.
 */

export const FT_TO_M = 0.3048
export const M_TO_FT = 1 / FT_TO_M
export const KT_TO_MPS = 0.514444
export const KT_TO_FPS = 1.6878099
export const FPS_TO_KT = 1 / KT_TO_FPS
export const NM_TO_M = 1852.0
export const M_TO_NM = 1 / NM_TO_M
export const NM_TO_FT = 6076.1155
export const DEG = Math.PI / 180
export const RAD = 180 / Math.PI

/** Mean Earth radius used for the local flat-Earth projection. */
export const EARTH_RADIUS_M = 6371000.0

export const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v)
export const lerp = (a, b, t) => a + (b - a) * t
export const invLerp = (a, b, v) => (b === a ? 0 : (v - a) / (b - a))

/** Linear map with clamping at both ends. */
export function remap(v, inLo, inHi, outLo, outHi) {
  return lerp(outLo, outHi, clamp(invLerp(inLo, inHi, v), 0, 1))
}

/** Frame-rate independent exponential smoothing. `rate` is per second. */
export function damp(current, target, rate, dt) {
  return lerp(current, target, 1 - Math.exp(-rate * dt))
}

/** Wrap to [-180, 180). */
export function wrapDeg180(deg) {
  let d = (deg + 180) % 360
  if (d < 0) d += 360
  return d - 180
}

/** Wrap to [0, 360). */
export function wrapDeg360(deg) {
  const d = deg % 360
  return d < 0 ? d + 360 : d
}

/** Wrap to [-PI, PI). */
export function wrapPi(rad) {
  let r = (rad + Math.PI) % (2 * Math.PI)
  if (r < 0) r += 2 * Math.PI
  return r - Math.PI
}

/** Shortest signed difference a - b in degrees. */
export function deltaDeg(a, b) {
  return wrapDeg180(a - b)
}

export function fmt(v, digits = 0) {
  if (!Number.isFinite(v)) return '---'
  return v.toFixed(digits)
}

/** Pad an integer with leading zeros, e.g. heading 007. */
export function pad(v, width = 3) {
  const s = Math.abs(Math.round(v)).toString()
  return s.padStart(width, '0')
}
